import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogContent,
  DialogBody,
  DialogActions,
  Button,
  Input,
  Label,
  makeStyles,
  tokens,
  Text,
} from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';

interface AzureOpenAIConfigDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (config: AzureOpenAIConfig) => void;
}

export interface AzureOpenAIConfig {
  apiKey: string;
  endpoint: string;
  deploymentName: string;
}

const STORAGE_KEY = 'azure-openai-config';

const useStyles = makeStyles({
  dialog: {
    maxWidth: '480px',
    minWidth: '400px',
  },
  
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  
  closeButton: {
    minWidth: 'auto',
    padding: '4px',
  },
  
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    paddingTop: '8px',
  },
  
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  
  label: {
    fontSize: '14px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
  },
  
  hint: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    marginTop: '2px',
  },
  
  input: {
    width: '100%',
  },
  
  actions: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'flex-end',
    paddingTop: '8px',
  },
});

// Helper functions to load/save config
export const loadAzureOpenAIConfig = (): AzureOpenAIConfig | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.warn('Failed to load Azure OpenAI config:', error);
  }
  return null;
};

export const saveAzureOpenAIConfig = (config: AzureOpenAIConfig): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  } catch (error) {
    console.warn('Failed to save Azure OpenAI config:', error);
  }
};

export const isAzureOpenAIConfigured = (): boolean => {
  const config = loadAzureOpenAIConfig();
  return !!(config?.apiKey && config?.endpoint);
};

const AzureOpenAIConfigDialog: React.FC<AzureOpenAIConfigDialogProps> = ({
  isOpen,
  onClose,
  onSave,
}) => {
  const styles = useStyles();
  const [apiKey, setApiKey] = useState('');
  const [endpoint, setEndpoint] = useState('');
  const [deploymentName, setDeploymentName] = useState('gpt-4.1');

  // Load saved config when dialog opens
  useEffect(() => {
    if (isOpen) {
      const config = loadAzureOpenAIConfig();
      if (config) {
        setApiKey(config.apiKey || '');
        setEndpoint(config.endpoint || '');
        setDeploymentName(config.deploymentName || 'gpt-4.1');
      }
    }
  }, [isOpen]);

  const handleSave = () => {
    const config: AzureOpenAIConfig = {
      apiKey,
      endpoint,
      deploymentName,
    };
    saveAzureOpenAIConfig(config);
    onSave(config);
    onClose();
  };

  const handleClear = () => {
    setApiKey('');
    setEndpoint('');
    setDeploymentName('gpt-4.1');
    localStorage.removeItem(STORAGE_KEY);
  };

  const isValid = apiKey.trim() && endpoint.trim();

  return (
    <Dialog open={isOpen} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface className={styles.dialog}>
        <DialogBody>
          <DialogTitle className={styles.header}>
            Configure Azure OpenAI
            <Button
              appearance="subtle"
              className={styles.closeButton}
              icon={<DismissRegular />}
              onClick={onClose}
            />
          </DialogTitle>
          
          <DialogContent className={styles.content}>
            <div className={styles.field}>
              <Label className={styles.label} htmlFor="endpoint">
                Endpoint URL
              </Label>
              <Input
                id="endpoint"
                className={styles.input}
                placeholder="https://your-resource.openai.azure.com"
                value={endpoint}
                onChange={(_, data) => setEndpoint(data.value)}
              />
              <Text className={styles.hint}>
                Your Azure OpenAI resource endpoint
              </Text>
            </div>
            
            <div className={styles.field}>
              <Label className={styles.label} htmlFor="apiKey">
                API Key
              </Label>
              <Input
                id="apiKey"
                className={styles.input}
                type="password"
                placeholder="Enter your API key"
                value={apiKey}
                onChange={(_, data) => setApiKey(data.value)}
              />
              <Text className={styles.hint}>
                Your Azure OpenAI API key (stored locally)
              </Text>
            </div>
            
            <div className={styles.field}>
              <Label className={styles.label} htmlFor="deploymentName">
                Deployment Name
              </Label>
              <Input
                id="deploymentName"
                className={styles.input}
                placeholder="gpt-4"
                value={deploymentName}
                onChange={(_, data) => setDeploymentName(data.value)}
              />
              <Text className={styles.hint}>
                The name of your model deployment
              </Text>
            </div>
          </DialogContent>
          
          <DialogActions className={styles.actions}>
            <Button appearance="subtle" onClick={handleClear}>
              Clear
            </Button>
            <Button appearance="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button appearance="primary" onClick={handleSave} disabled={!isValid}>
              Save
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default AzureOpenAIConfigDialog;
